import { PagesId } from '@ts-enums/PagesId'
import { Translations } from '@ts-types/Translations'
import { resolvePagePath } from '@utils/resolvePagePath'
import { Languages } from '@wicadu/langscope'

const globalTranslations: Translations = {
  es: {
    title: 'Políticas y Cumplimiento',
    description:
      'Encuentra información sobre nuestras políticas legales, términos y condiciones, privacidad, cumplimiento normativo y otros documentos importantes que regulan el uso de nuestros servicios',
    legal_information: 'Información Legal',
    seo_keywords:
      'políticas y cumplimiento, términos y condiciones, información legal, cumplimiento regulatorio, política de privacidad, documentos legales, uso comercial, incumplimiento de los términos, soporte legal, términos de la plataforma, aceptar términos y condiciones, problemas legales, requisitos legales para empresas, política de suspensión de cuenta',
    page_id: PagesId.LegalHome,
    breadcrumbs: {
      home: 'Inicio',
      legal_home: 'Políticas y Cumplimiento',
    },
    faq: [
      {
        question: '¿Puedo usar wicadu con fines comerciales?',
        answer: `Sí, wicadu está <strong>diseñado para uso comercial</strong>, siempre que se respeten nuestros <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'es')?.pathname
        }" target="_blank">Términos y Condiciones</a> y las leyes locales aplicables a su negocio.`,
      },
      {
        question: '¿Qué pasa si incumplo los Términos y Condiciones?',
        answer: `Si se incumplen los <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'es')?.pathname
        }" target="_blank">Términos y Condiciones</a>, podríamos suspender o cancelar su cuenta, dependiendo de la gravedad de la situación, tras una revisión justa.`,
      },
      {
        question: '¿Necesito aceptar los Términos y Condiciones para usar la wicadu?',
        answer: `Sí, al usar nuestra plataforma, usted acepta nuestros <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'es')?.pathname
        }" target="_blank">Términos y Condiciones</a>.`,
      },
      {
        question: '¿Qué sucede si cambian los términos o políticas?',
        answer:
          'Notificamos a los usuarios sobre actualizaciones importantes, y el uso continuo de la plataforma implica la aceptación de los nuevos términos.',
      },
      {
        question: '¿A quién puedo contactar por temas legales?',
        answer:
          'Si tiene preguntas o problemas legales, puede ponerse en contacto con nuestro <strong>equipo de soporte</strong>.',
      },
    ],
  },
  en: {
    title: 'Policies and Compliance',
    description:
      'Find information about our legal policies, terms and conditions, privacy, regulatory compliance, and other important documents that govern the use of our services',
    seo_keywords:
      'policies and compliance, terms and conditions, legal information, regulatory compliance, privacy policy, legal documents, commercial use terms, terms violations, legal support, platform terms, accept terms and conditions, legal issues, regulatory compliance information, business legal requirements, account suspension policy',
    legal_information: 'Legal Information',
    page_id: PagesId.LegalHome,
    breadcrumbs: {
      home: 'Home',
      legal_home: 'Policies and Compliance',
    },
    faq: [
      {
        question: 'Can I Use wicadu for Commercial Purposes?',
        answer: `Yes, <strong>wicadu</strong> is designed for <strong>commercial use</strong>, as long as our <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'en')?.pathname
        }" target="_blank">Terms and Conditions</a> and the local laws applicable to your business are respected.`,
      },
      {
        question: 'What Happens If I violate the Terms and Conditions?',
        answer: `If the <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'en')?.pathname
        }" target="_blank">Terms and Conditions</a> are violated, we may <strong>suspend or cancel your account</strong> depending on the severity of the situation, following a fair review.`,
      },
      {
        question: 'Do I need to accept the Terms and Conditions to use the platform?',
        answer: `Yes, by using our platform, you agree to our <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, 'en')?.pathname
        }" target="_blank">Terms and Conditions</a>.`,
      },
      {
        question: 'What happens if the terms or policies change?',
        answer:
          'We <strong>notify users</strong> of significant updates, and <strong>continued use</strong> of the platform means acceptance of new terms.',
      },
      {
        question: 'Who Can I Contact for Legal Issues?',
        answer:
          'If you have legal questions or issues, you can contact our <strong>support team</strong>.',
      },
    ],
  },
  fr: {
    title: 'Politiques et conformité',
    description:
      "Retrouvez des informations sur nos politiques juridiques, nos conditions générales, la confidentialité, la conformité réglementaire et d'autres documents importants qui encadrent l'utilisation de nos services",
    seo_keywords:
      "politiques et conformité, conditions générales, informations juridiques, conformité réglementaire, politique de confidentialité, documents juridiques, conditions d'utilisation commerciale, violations des conditions, assistance juridique, exigences légales des entreprises",
    legal_information: 'Informations juridiques',
    page_id: PagesId.LegalHome,
    breadcrumbs: {
      home: 'Accueil',
      legal_home: 'Politiques et conformité',
    },
    faq: [
      {
        question: 'Puis-je utiliser wicadu à des fins commerciales ?',
        answer: `Oui, <strong>wicadu</strong> est conçu pour un <strong>usage commercial</strong>, tant que nos <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, Languages.French)?.pathname
        }" target="_blank">Conditions générales</a> et les lois locales applicables à votre activité sont respectées.`,
      },
      {
        question: 'Que se passe-t-il si je ne respecte pas les Conditions générales ?',
        answer: `Si les <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, Languages.French)?.pathname
        }" target="_blank">Conditions générales</a> sont enfreintes, nous pouvons <strong>suspendre ou résilier votre compte</strong> selon la gravité de la situation, après un examen équitable.`,
      },
      {
        question: 'Dois-je accepter les Conditions générales pour utiliser la plateforme ?',
        answer: `Oui, en utilisant notre plateforme, vous acceptez nos <a href="${
          resolvePagePath(PagesId.LegalTermsAndConditions, Languages.French)?.pathname
        }" target="_blank">Conditions générales</a>.`,
      },
      {
        question: 'Que se passe-t-il si les conditions ou politiques changent ?',
        answer:
          "Nous informons les utilisateurs des mises à jour importantes, et la poursuite de l'utilisation de la plateforme vaut acceptation des nouvelles conditions.",
      },
      {
        question: 'Qui puis-je contacter pour des questions juridiques ?',
        answer:
          "Si vous avez des questions ou des problèmes juridiques, vous pouvez contacter notre équipe d'assistance.",
      },
    ],
  },
  pt: {},
}

export default globalTranslations
